/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useCallback} from 'react';
import {
  FormControlLabel,
  makeStyles,
  Switch,
  SwitchProps,
  withStyles
} from '@material-ui/core';
import classNames from 'classnames';
import {dlsColor, dlsShadow} from '@ion/dls/src/theme/theme';

enum LabelPlacement {
  END = 'end',
  START = 'start'
};

interface StyleProps {
  marginRight?: string;
  marginTop?: string;
  marginLeft?: string;
};

interface SwitchTypes extends SwitchProps, StyleProps {
  label?: string;
  checked?: boolean;
  name?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  labelPlacement?: LabelPlacement;
  isDarkLabel?: boolean;
};

const useStyles = makeStyles<SwitchTypes>(() => ({
  formControlRoot : {
    width          : '100%',
    justifyContent : 'space-between'
  },
  labelAlignment : {
    marginTop   : (props: StyleProps) => props.marginTop || '0px',
    marginLeft  : (props: StyleProps) => props.marginLeft || '1px',
    marginRight : (props: StyleProps) => props.marginRight || '-1px'
  },
  defaultLabel : {
    color      : dlsColor('grey2'),
    fontSize   : '14px',
    fontFamily : 'Inter'
  },
  darkLabel : {
    color      : `${dlsColor('black')} !important`,
    marginLeft : '6px'
  },
  root : {
    overflow : 'visible',
    '& *'    : {
      boxSizing : 'border-box'
    }
  }
}));

const CustomSwitch = withStyles(theme => ({
  root : {
    width   : 40,
    height  : 24,
    padding : 0,
    display : 'flex'
  },
  switchBase : {
    color      : dlsColor('white'),
    width      : 24,
    height     : 24,
    padding    : 0,
    transform  : 'translateX(0px)',
    transition : theme.transitions.create(['transform', 'background-color'], {
      duration : 200
    }),
    '&:hover' : {
      backgroundColor : dlsColor('grey4'),
      '& $thumb'      : {
        width        : 22,
        height       : 18,
        transform    : 'translateX(-2px, -0.75px)',
        borderRadius : 10
      }
    },
    '&$checked' : {
      color     : dlsColor('white'),
      transform : 'translateX(16px)',
      '&:hover' : {
        backgroundColor : dlsColor('arcticBlue'),
        '& $thumb'      : {
          width        : 22,
          height       : 18,
          border       : `0.5px solid ${dlsColor('arcticBlue')}`,
          transform    : 'translate(-50%, -50%)',
          borderRadius : 10
        }
      },
      '& + $track' : {
        opacity         : 1,
        backgroundColor : dlsColor('arcticBlue'),
        // Figma specs an inner glow on the ON-state track, absent until now ...
        boxShadow       : 'inset 0px 0px 4px rgba(8, 99, 170, 0.5)'
      }
    },
    '&:focus-within, &.Mui-focusVisible, &.Mui-focused' : {
      '& + $track' : {
        border          : `2px solid ${dlsColor('skyBlue')}`,
        backgroundColor : dlsColor('grey4')
      },
      '&$checked + $track' : {
        backgroundColor : dlsColor('arcticBlue')
      },
      '& $thumb' : {
        width        : 22,
        height       : 18,
        transform    : 'translateX(-2px, -0.75px)',
        borderRadius : 10
      }
    },
    '&$disabled' : {
      color        : dlsColor('grey4'),
      opacity      : 0.38,
      '& + $track' : {
        opacity         : 1,
        backgroundColor : dlsColor('grey4')
      }
    }
  },
  thumb : {
    top             : '50%',
    left            : '50%',
    // Figma specs an 18x18 knob, not 16x16 ...
    width           : 18,
    height          : 18,
    position        : 'absolute',
    boxShadow       : dlsShadow(8, 8.2, 8.3),
    transform       : 'translate(-50%, -50%)',
    borderRadius    : '50%',
    backgroundColor : dlsColor('white'),
    transition      : theme.transitions.create(['width', 'height', 'border-radius'], {
      duration : 200
    }),
    '&$checked' : {
      // rgba(8, 99, 170, ...) below is darkestBlue (#0863AA) at the exact
      // opacities Figma specs for the ON-state knob - dlsShadow's own
      // colorOverride replaces a shadow's color+alpha with one flat value,
      // so it can't express three different alphas across these layers,
      // and shadow8's own shape (2px blur, 0 spread) doesn't match this
      // spec's first layer (0 blur, 1px spread) regardless - written
      // directly against the Figma spec instead ...
      border    : '0.5px solid rgba(8, 99, 170, 0.25)',
      boxShadow : [
        '0px 0px 0px 1px rgba(8, 99, 170, 0.04)',
        '0px 3px 8px rgba(8, 99, 170, 0.15)',
        '0px 3px 1px rgba(8, 99, 170, 0.06)'
      ].join(', ')
    }
  },
  track : {
    opacity         : 1,
    borderRadius    : 100,
    backgroundColor : dlsColor('grey4'),
    transition      : theme.transitions.create(['background-color', 'border'], {
      duration : 200
    })
  },
  checked  : {},
  disabled : {}
}))(Switch);

/**
 * A fluid Material UI Switch that features a "morphing" thumb. The thumb 
 * expands from a 16x16 circle to a 22x18 rounded rectangle on hover and focus.
 */
export const SwitchWrapper = ({
  label = '',
  labelPlacement = LabelPlacement.END,
  checked = false,
  isDarkLabel = true,
  ...props
}: SwitchTypes) => {
  const classes = useStyles(props);

  const {
    marginRight,
    marginLeft,
    marginTop,
    ...rest
  } = props;

  const noop = useCallback(function(event) {
    event.stopPropagation();
  }, []);

  return (
    <FormControlLabel label={label}
      className={classNames(classes.labelAlignment, classes.defaultLabel)}
      classes={{
        root  : labelPlacement === LabelPlacement.START ? classes.formControlRoot : undefined,
        label : isDarkLabel ? classes.darkLabel : undefined
      }}
      onClick={noop}
      onFocus={noop}
      value={label}
      control={
        <CustomSwitch disableRipple disableFocusRipple
          className={classes.root}
          checked={checked}
          {...rest} />
      }
      labelPlacement={labelPlacement} />
  );
};

export default SwitchWrapper;
